// ⌘K / Ctrl+K "Jump to…": the header trigger plus a palette that goes to
// any page, agent (and agent section), subagent, workflow, recent run or
// task, project, connection, deployment or Settings section. The reads are
// made only while it is open. Keyboard: type to filter, ↑/↓ to move, Enter
// to go, Esc to close.
//
// Portaled to <body>: the shell's backdrop-filter surfaces would trap a
// position:fixed panel. The panel is opaque (bg-overlay) because Earth
// and Midnight turn blur off.

import { Suspense, lazy, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CornerDownLeft, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useCapabilities } from "@/api/capabilities";
import { useConnectionDirectory } from "@/api/connectionDirectory";
import { useCustomerAgents } from "@/api/customers";
import { useDeployments } from "@/api/deployments";
import { useProjects } from "@/api/projects";
import { useTasks } from "@/api/tasks";
import { useWorkflowRuns, useWorkflows } from "@/api/workflows";
import { useSettingsFlags } from "@/features/settings/useSettingsFlags";
import { useSubagentBlueprints } from "@/api/subagentBlueprints";
import { useWorkflowCapacity } from "@/api/workerSlots";
import { AgentAvatar } from "@/components/ui/AgentAvatar";
import { Button } from "@/components/ui/Button";
import { Kbd } from "@/components/ui/Kbd";
import { OVERLAY_SHADOW, Z, cx } from "@/components/ui/controlStyles";
import { isTopLayer, useFocusTrap, useScrollLock } from "@/components/ui/overlayHooks";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";
import { isBlueprint } from "@/lib/agentKind";
import { formatDate } from "@/lib/format";
import { workflowRunPath } from "@/features/workflows/paths";

import { buildJumpItems, filterJumpItems, type JumpItem } from "./jumpItems";
import { buildObjectItems } from "./jumpObjects";
import { withDeploymentsFlag } from "./navModel";
import { JUMP_HINT, useJumpShortcut } from "./shortcuts";

const GROUPS: JumpItem["group"][] = ["Go to", "Workflows", "Agents", "Subagents", "Projects", "Connections", "Deployments", "Recent", "Settings"];
const RECENT_TASKS = { limit: 20, includeEndedScheduled: false } as const;

const RunLauncher = lazy(() => import("@/features/workflows/editor/RunLauncher").then((m) => ({ default: m.RunLauncher })));

export function JumpPalette({ customerId }: { customerId: string }) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState<{ slug: string; name: string } | null>(null);
  const navigate = useNavigate();
  useJumpShortcut(() => setOpen((v) => !v));
  return (
    <>
      <Button
        variant="secondary"
        size="md"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts={JUMP_HINT === "⌘K" ? "Meta+K" : "Control+K"}
        aria-label="Jump to anything"
        className="shrink-0 justify-start text-fg-subtle max-lg:w-[32px] max-lg:px-0 max-lg:justify-center lg:w-[240px] xl:w-[280px]"
      >
        <Search aria-hidden className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        <span aria-hidden className="max-lg:hidden">Jump to…</span>
        <span aria-hidden className="ml-auto flex max-lg:hidden">
          <Kbd>{JUMP_HINT}</Kbd>
        </span>
      </Button>
      {open && createPortal(<PalettePanel customerId={customerId} onClose={() => setOpen(false)} onRun={setRunning} />, document.body)}
      {running && (
        <Suspense fallback={null}>
          <RunLauncher
            customerId={customerId}
            slug={running.slug}
            name={running.name}
            onClose={() => setRunning(null)}
            onStarted={(run) => {
              setRunning(null);
              navigate(workflowRunPath("/workflows", run.run_id));
            }}
          />
        </Suspense>
      )}
    </>
  );
}

function PalettePanel({ customerId, onClose, onRun }: { customerId: string; onClose: () => void; onRun: (w: { slug: string; name: string }) => void }) {
  const navigate = useNavigate();
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  useScrollLock(true);
  useFocusTrap(panel, true, input);

  const { agents } = useAgentDirectory(customerId);
  const settingsFlags = useSettingsFlags(customerId);
  // Deployments is on by the capacity route, not the settings (as the sidebar's useNavFlags reads it).
  const deployments = useWorkflowCapacity(customerId).data?.deployments;
  const flags = useMemo(() => withDeploymentsFlag(settingsFlags, deployments), [settingsFlags, deployments]);
  // Same cached manifest the directory reads; it says whose desktop is exposed.
  const manifest = useCustomerAgents(customerId).data;
  const withoutDesktop = useMemo(
    () => new Set((manifest?.agents ?? []).filter((a) => a.vnc_host_port === null).map((a) => a.template)),
    [manifest],
  );
  // Briefs for the subagents' hints; read only when there are subagents.
  const blueprints = useSubagentBlueprints(agents.some(isBlueprint) ? customerId : null).data?.blueprints;
  // Corvus storage: no agent Mail, Linked account in Settings (Google Drive's answer while loading).
  const store = useCapabilities(customerId).file_store;
  // The things themselves, each only where its page is on.
  const tasksOn = flags?.tasks_enabled === true;
  const workflows = useWorkflows(flags?.workflows_enabled === true ? customerId : null);
  const runs = useWorkflowRuns(flags?.workflows_enabled === true ? customerId : null, { limit: 20 });
  const tasks = useTasks(tasksOn ? customerId : null, RECENT_TASKS);
  const projects = useProjects(customerId);
  const directory = useConnectionDirectory(customerId, flags?.connections_enabled === true);
  const deployed = useDeployments(deployments === true ? customerId : null);
  const reads = [workflows, runs, tasks, projects, directory, deployed];
  const looking = reads.some((q) => q.isLoading);
  const someFailed = reads.some((q) => q.isError && q.data === undefined);
  const connections = directory.data?.state === "on" ? directory.data.directory.entries : undefined;
  const all = useMemo(() => {
    const briefs = new Map((blueprints ?? []).map((b) => [b.template, b.subagent.brief]));
    return [
      ...buildJumpItems(agents, flags, withoutDesktop, (t) => briefs.get(t), store),
      ...buildObjectItems({
        workflows: workflows.data?.workflows,
        runs: runs.data?.runs,
        tasks: tasks.data?.tasks,
        projects: projects.data?.projects,
        connections,
        deployments: deployed.data,
        day: (iso) => formatDate(iso),
      }),
    ];
  }, [agents, flags, withoutDesktop, blueprints, store, workflows.data, runs.data, tasks.data, projects.data, connections, deployed.data]);
  const results = useMemo(() => {
    const hits = filterJumpItems(all, query);
    return GROUPS.flatMap((g) => hits.filter((i) => i.group === g));
  }, [all, query]);

  useEffect(() => setActive(0), [query]);
  // A list that shrank under the cursor (a read came back) never leaves it on nothing.
  useEffect(() => {
    if (active >= results.length) setActive(0);
  }, [active, results.length]);
  useEffect(() => {
    list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function go(item: JumpItem | undefined) {
    if (!item) return;
    onClose();
    if (item.run) {
      onRun(item.run);
      return;
    }
    navigate(item.to);
    // The current Settings page reads its tab on "hashchange", which a
    // router navigation (pushState) does not fire. The reworked Settings
    // also follows the router's hash, so this is then a harmless repeat;
    // drop it once that has merged.
    if (item.to.includes("#")) window.dispatchEvent(new HashChangeEvent("hashchange"));
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (results.length ? (i + step + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape" && isTopLayer(panel.current)) {
      e.preventDefault();
      onClose();
    }
  }

  let index = -1;
  return (
    <div
      className="fixed inset-0 flex items-start justify-center bg-[color:var(--c-scrim)] px-4 pt-[12vh]"
      style={{ zIndex: Z.modal }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Jump to"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cx(
          "flex max-h-[min(560px,76vh)] w-full max-w-[600px] flex-col overflow-hidden rounded-card border border-line bg-overlay outline-none",
          OVERLAY_SHADOW,
        )}
        style={{ animation: "fadeIn 120ms ease-out both" }}
      >
        <div className="flex h-[52px] shrink-0 items-center gap-3 border-b border-line-subtle px-4">
          <Search aria-hidden className="h-4 w-4 shrink-0 text-fg-subtle" strokeWidth={1.75} />
          <input
            ref={input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to anything"
            aria-label="Jump to"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={results[active] ? `${listId}-${active}` : undefined}
            aria-autocomplete="list"
            className="h-full min-w-0 flex-1 bg-transparent text-md text-fg outline-none placeholder:text-fg-subtle"
          />
          <Kbd>Esc</Kbd>
        </div>
        <div ref={list} id={listId} role="listbox" aria-label="Results" className="min-h-0 flex-1 overflow-y-auto p-2">
          {results.length === 0 && (
            <p role="status" className="px-3 py-8 text-center text-sm text-fg-muted">
              {looking ? "Looking…" : someFailed ? `Nothing matches “${query.trim()}”, but some lists didn't load.` : `Nothing matches “${query.trim()}”.`}
            </p>
          )}
          {GROUPS.map((group) => {
            const rows = results.filter((r) => r.group === group);
            if (!rows.length) return null;
            return (
              <div key={group} role="group" aria-label={group} className="pb-1">
                <div className="ui-label px-3 pb-1 pt-2">{group}</div>
                {rows.map((item) => {
                  index += 1;
                  const i = index;
                  return (
                    <ResultRow
                      key={item.id}
                      id={`${listId}-${i}`}
                      index={i}
                      item={item}
                      selected={i === active}
                      onHover={() => setActive(i)}
                      onPick={() => go(item)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-4 border-t border-line-subtle px-4 py-2 text-xs text-fg-subtle">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> to move
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>
              <CornerDownLeft aria-hidden className="h-3 w-3" />
            </Kbd>
            to open
          </span>
        </div>
      </div>
    </div>
  );
}

interface ResultRowProps {
  id: string;
  index: number;
  item: JumpItem;
  selected: boolean;
  onHover: () => void;
  onPick: () => void;
}

function ResultRow({ id, index, item, selected, onHover, onPick }: ResultRowProps) {
  const Icon = item.icon;
  return (
    <div
      id={id}
      data-index={index}
      role="option"
      aria-selected={selected}
      onMouseMove={onHover}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPick}
      className={cx(
        "flex h-[40px] cursor-pointer items-center gap-3 rounded-control px-3 text-base",
        selected ? "bg-selected text-fg" : "text-fg-muted",
      )}
    >
      {item.agent ? (
        <AgentAvatar name={item.agent.name} hue={item.agent.hue} src={item.agent.avatarUrl} kind={item.agent.kind} size={24} />
      ) : Icon ? (
        <span className="flex w-6 shrink-0 justify-center">
          <Icon aria-hidden className={cx("h-4 w-4", selected ? "text-fg" : "text-fg-subtle")} strokeWidth={1.75} />
        </span>
      ) : null}
      {/* The name is what was searched for: the hint gives way first. */}
      <span className="max-w-[60%] shrink-0 truncate">{item.label}</span>
      {item.hint && <span className="min-w-0 truncate text-sm text-fg-subtle">{item.hint}</span>}
      {selected && <CornerDownLeft aria-hidden className="ml-auto h-3.5 w-3.5 shrink-0 text-fg-subtle" />}
    </div>
  );
}
